<template>
  <page-container :title="title" :class="{ mobile: isMobile }">
    <v-row
      class="ma-0 py-2 tab-header"
      :class="[isMobile ? 'pa-4' : 'px-6']"
      justify="center"
      align="center"
    >
      <h2>Task details</h2>
      <v-spacer></v-spacer>
      <feature name="jobs" variant="write" data="jobs">
        <v-btn
          tile
          elevation="0"
          v-show="jobInfoChanged"
          class="button background-color--inactive color--foreground"
          @click="goBack()"
          :disabled="saving"
          >Cancel</v-btn
        >
        <v-btn
          tile
          color="primary"
          class="mx-2 button"
          elevation="0"
          v-show="jobInfoChanged"
          :disabled="!formValid || saving"
          @click="saveEditedJobInfo"
          :loading="saving"
          >Save</v-btn
        >
        <v-btn icon @click="deleteJob" :disabled="saving" v-if="!isNew"
          ><v-icon>mdi-delete</v-icon></v-btn
        >
      </feature>
    </v-row>
    <v-row class="ma-0">
      <v-sheet style="height: 100%; width: 100%">
        <v-container fluid :class="{ 'pt-0': isMobile }">
          <v-form
            v-model="formValid"
            ref="form"
            lazy-validation
            :disabled="!canEdit"
          >
            <v-row class="ma-0 pa-0">
              <v-col cols="12" md="6" :class="{ 'pa-0': isMobile }">
                <v-row align="center" class="ma-0">
                  <v-col cols="3" class="text-section-title">Task</v-col>
                  <v-col :cols="isMobile ? 9 : 8" class="py-2">
                    <v-text-field
                      placeholder="Add a title"
                      solo
                      flat
                      dense
                      validate-on-blur
                      v-model="editedJobInfo.name"
                      class="details-field"
                      background-color="transparent"
                      :hide-details="
                        rules.required(editedJobInfo.name) === true ||
                        !jobInfoChanged
                      "
                      :rules="[rules.required]"
                      required
                    />
                  </v-col>
                </v-row>
                <v-row align="center" class="ma-0 mr-4">
                  <v-col cols="3" class="text-section-title">Project</v-col>
                  <v-col :cols="canEdit ? 7 : 9" class="py-2 pr-0 pl-4">
                    <v-select
                      ref="projectSelect"
                      solo
                      flat
                      dense
                      class="details-field"
                      background-color="transparent"
                      :hide-details="true"
                      :items="projects"
                      :loading="projectsLoading"
                      v-model="editedJobInfo.project"
                      return-object
                      item-text="name"
                      item-value="id"
                      label="Select project"
                    >
                      <template v-slot:prepend-item>
                        <v-list-item ripple @click="removeProjectSelection">
                          <v-list-item-content>
                            <v-list-item-title> No Project </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="2" v-if="canEdit">
                    <v-btn
                      color="primary"
                      outlined
                      tile
                      class="button"
                      @click="newProjectPopupVisible = true"
                      >Add</v-btn
                    >
                  </v-col>
                </v-row>
                <v-row align="center" class="ma-0 mr-4">
                  <v-col cols="3" class="text-section-title">Location</v-col>
                  <v-col :cols="canEdit ? 7 : 9" class="py-2 pr-0 pl-4">
                    <v-select
                      ref="locationSelect"
                      solo
                      flat
                      dense
                      class="details-field"
                      background-color="transparent"
                      :hide-details="true"
                      :items="locations"
                      :loading="locationsLoading"
                      v-model="editedJobInfo.location"
                      return-object
                      item-text="name"
                      item-value="id"
                      label="Select location"
                    >
                      <template v-slot:prepend-item>
                        <v-list-item ripple @click="removeLocationSelection">
                          <v-list-item-content>
                            <v-list-item-title> No Location </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="2" v-if="canEdit">
                    <v-btn
                      color="primary"
                      outlined
                      tile
                      class="button"
                      @click="newLocationPopupVisible = true"
                      >Add</v-btn
                    >
                  </v-col>
                </v-row>
                <v-row align="center" class="ma-0">
                  <v-col cols="3" class="text-section-title">Start</v-col>
                  <v-col cols="5" class="py-2">
                    <v-row class="ma-0" align="center">
                      <v-col
                        :cols="isMobile ? 12 : 10"
                        class="pa-0"
                        :class="{ 'pr-2': !isMobile }"
                      >
                        <v-menu
                          v-model="startDateMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              solo
                              flat
                              dense
                              :value="editedJobInfo.startDate"
                              background-color="transparent"
                              class="details-field"
                              :hide-details="true"
                              placeholder="dd/mm/yyyy"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              :clearable="true"
                              @click:clear="removeStartDate"
                            />
                          </template>
                          <v-date-picker
                            ref="picker"
                            :value="editedJobInfo.startDate"
                            @input="selectStartDate($event)"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col cols="2" class="pa-0" v-if="!isMobile">
                        <v-icon>mdi-calendar</v-icon>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="4" class="py-2">
                    <v-row class="ma-0" align="center">
                      <v-col
                        :cols="isMobile ? 12 : 10"
                        class="pa-0"
                        :class="{ 'pr-2': !isMobile }"
                      >
                        <vue-timepicker
                          v-model="editedJobInfo.startTime"
                          @change="selectStartTime"
                          input-width="100%"
                        ></vue-timepicker>
                      </v-col>
                      <v-col cols="2" class="pa-0" v-if="!isMobile">
                        <v-icon>mdi-clock</v-icon>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row align="center" class="ma-0">
                  <v-col cols="3" class="text-section-title">End</v-col>
                  <v-col cols="5" class="py-2">
                    <v-row class="ma-0" align="center">
                      <v-col
                        :cols="isMobile ? 12 : 10"
                        class="pa-0"
                        :class="{ 'pr-2': !isMobile }"
                      >
                        <v-menu
                          v-model="dateMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              solo
                              flat
                              dense
                              :value="editedJobInfo.dueDate"
                              background-color="transparent"
                              :hide-details="true"
                              placeholder="dd/mm/yyyy"
                              class="details-field"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              :clearable="true"
                              @click:clear="removeDueDate"
                            />
                          </template>
                          <v-date-picker
                            ref="picker"
                            :value="editedJobInfo.dueDate"
                            :min="editedJobInfo.startDate"
                            @input="selectDueDate($event)"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col cols="2" v-if="!isMobile" class="pa-0">
                        <v-icon>mdi-calendar</v-icon>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="4" class="py-2">
                    <v-row class="ma-0" align="center">
                      <v-col
                        :cols="isMobile ? 12 : 10"
                        class="pa-0"
                        :class="{ 'pr-2': !isMobile }"
                      >
                        <vue-timepicker
                          :minute-interval="10"
                          input-width="100%"
                          v-model="editedJobInfo.dueTime"
                          @change="selectDueTime"
                        ></vue-timepicker>
                      </v-col>
                      <v-col cols="2" class="pa-0" v-if="!isMobile"
                        ><v-icon>mdi-clock</v-icon></v-col
                      >
                    </v-row>
                  </v-col>
                </v-row>
                <v-row align="center" class="ma-0" v-if="!isNew">
                  <v-col cols="3" class="text-section-title">Status</v-col>
                  <v-col cols="9" lg="4" md="6" class="py-2">
                    <v-select
                      id="statusSelect"
                      solo
                      flat
                      dense
                      :items="statuses"
                      v-model="editedJobInfo.status"
                      class="details-field"
                      background-color="transparent"
                      :hide-details="true"
                      required
                    />
                  </v-col>
                </v-row>
                <v-row align-content="center" class="ma-0">
                  <v-col cols="3" class="text-section-title">Brief</v-col>
                  <v-col cols="9">
                    <v-textarea
                      solo
                      flat
                      dense
                      v-model="editedJobInfo.description"
                      placeholder="Project description"
                      class="details-field"
                      background-color="transparent"
                    ></v-textarea>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="6">
                <v-row align="center" class="ma-0">
                  <v-col cols="3" class="text-section-title">Owner</v-col>
                  <v-col cols="9" class="py-2">
                    <v-row class="ma-0 pa-0">
                      <user-avatar
                        :userInfo="editedJobInfo.owner"
                        v-if="editedJobInfo.owner"
                      />

                      <feature name="jobs" variant="write" data="jobs">
                        <v-btn
                          fab
                          outlined
                          max-width="32"
                          max-height="32"
                          color="primary"
                          elevation="0"
                          @click.prevent="ownerSelectionVisible = true"
                          ><v-icon small>mdi-pencil</v-icon></v-btn
                        >
                      </feature>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row align="center" class="ma-0">
                  <v-col cols="3" class="text-section-title">Assignees</v-col>
                  <v-col cols="9" class="py-2">
                    <user-avatar-group :users="editedJobInfo.assignees" />
                    <v-btn
                      fab
                      outlined
                      elevation="0"
                      color="primary"
                      max-width="32"
                      max-height="32"
                      class="mx-1"
                      v-if="canEdit"
                      @click="
                        assigneesSelectionVisible = !assigneesSelectionVisible
                      "
                      >+</v-btn
                    >
                  </v-col>
                </v-row>
                <v-row class="ma-0">
                  <v-col
                    cols="3"
                    class="text-section-title pt-4"
                    style="overflow-wrap: break-word"
                    >Attachments</v-col
                  >
                  <v-col cols="9" class="py-2">
                    <feature name="jobs" variant="write" data="jobs">
                      <file-upload
                        :relativePath="'jobs/' + id"
                        :loading="loadingFiles"
                        :reservedNames="files.map((e) => e.name)"
                        @change="selectedFilesChange"
                      ></file-upload>
                    </feature>
                  </v-col>
                  <v-col cols="12" class="ma-0 pa-0">
                    <!-- TODO: reload when new items upload finished! -->
                    <files-list
                      v-model="files"
                      :relativePath="'jobs/' + this.id"
                      :uploadTasks="uploads"
                      :loading.sync="loadingFiles"
                    ></files-list>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-sheet>
    </v-row>
    <v-row justify="center">
      <v-dialog
        v-model="assigneesSelectionVisible"
        max-width="600px"
        persistent
      >
        <selection-list
          title="Select project assignees"
          :collectionRef="employeesCollectionRef"
          :headers="[
            {
              text: 'First name',
              value: 'firstName',
            },
            {
              text: 'Last name',
              value: 'lastName',
            },
          ]"
          v-model="editedJobInfo.assignees"
          @close="assigneesSelectionVisible = false"
          :readOnlyId="false"
        ></selection-list>
      </v-dialog>
      <v-dialog v-model="ownerSelectionVisible" max-width="600px" persistent>
        <selection-list
          title="Select project owner"
          :collectionRef="employeesCollectionRef"
          :headers="[
            {
              text: 'First name',
              value: 'firstName',
            },
            {
              text: 'Last name',
              value: 'lastName',
            },
          ]"
          :singleSelect="true"
          v-model="editedJobInfo.owner"
          @close="ownerSelectionVisible = false"
          :readOnlyId="false"
        ></selection-list>
      </v-dialog>
      <v-dialog v-model="newProjectPopupVisible" max-width="600px" persistent>
        <common-editing-popup
          title="New project"
          saveBtnTitle="Create project"
          collectionName="projects"
          @close="newProjectPopupVisible = false"
          @submit="createProjectAndSelect"
          :originalItem="{ name: '', description: '' }"
          :loading="creatingProject"
          :key="Date.now().toString()"
        >
          <template v-slot:default="{ editedItem, formStatus }">
            <new-project-popup
              :editedData="editedItem"
              :formStatus="formStatus"
            ></new-project-popup>
          </template>
        </common-editing-popup>
      </v-dialog>
      <v-dialog v-model="newLocationPopupVisible" max-width="600px" persistent>
        <common-editing-popup
          title="New location"
          saveBtnTitle="Create location"
          collectionName="locations"
          @close="newLocationPopupVisible = false"
          @submit="createNewLocationAndSelect"
          :originalItem="{ name: '' }"
          :loading="creatingLocation"
          :key="Date.now().toString()"
        >
          <template v-slot:default="{ editedItem, formStatus }">
            <new-location-popup
              :editedData="editedItem"
              :formStatus="formStatus"
            >
            </new-location-popup>
          </template>
        </common-editing-popup>
      </v-dialog>
    </v-row>
    <v-row class="ma-0 my-2" v-if="!isNew"
      ><job-progress :progress="jobProgress" :jobId="id"></job-progress
    ></v-row>
  </page-container>
</template>

<script>
import VueTimepicker from 'vue2-timepicker'
import PageContainer from '@/components/PageContainer.vue'
import SelectionList from '@/components/SelectionList.vue'
import JobProgress from './components/JobProgress.vue'
import UserAvatarGroup from '@/components/UserAvatarGroup.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import CommonEditingPopup from '@/components/CommonEditingPopup.vue'
import { constants, objects } from '@/helpers'
import { textFieldRules, getInitials } from '@/common'
import { db, serverTimestamp, storage } from '@/firebaseConfig'
import { mapActions, mapGetters } from 'vuex'
import {
  hasAccessRightForCollection,
  AccessRightWrite,
} from '@/plugins/feature'
import { denormalized } from '@/helpers'
import NewProjectPopup from './components/NewProjectPopup.vue'
import NewLocationPopup from './components/NewLocationPopup.vue'
import FileUpload from '@/components/FileUpload.vue'
import FilesList from '@/components/FilesList.vue'

export default {
  name: 'JobDetailsView',
  props: ['projectId', 'id'],
  components: {
    PageContainer,
    SelectionList,
    JobProgress,
    UserAvatarGroup,
    UserAvatar,
    CommonEditingPopup,
    NewProjectPopup,
    NewLocationPopup,
    FileUpload,
    FilesList,
    VueTimepicker,
  },
  data() {
    return {
      title: [
        {
          to: 'jobs',
          text: 'Tasks',
        },
        {
          text: this.id === 'new' ? 'New task' : 'View task',
        },
      ],
      currentTab: 0,
      startDateMenu: false,
      dateMenu: false,
      loading: false,
      saving: false,
      formValid: false,
      rules: textFieldRules,
      jobInfo: {
        status: constants.STATUS_PENDING,
        assignees: [],
      },
      editedJobInfo: {
        status: constants.STATUS_PENDING,
        assignees: [],
      },
      unsubscribe: null,
      assigneesSelectionVisible: false,
      ownerSelectionVisible: false,
      newProjectPopupVisible: false,
      newLocationPopupVisible: false,
      getInitials,
      projects: [],
      projectsLoading: false,
      locations: [],
      locationsLoading: false,
      statuses: constants.STATUSES,
      creatingProject: false,
      creatingLocation: false,
      uploads: [],
      loadingFiles: false,
      files: [],
      selectedFiles: [],
    }
  },
  created() {
    if (!this.isNew) {
      this.getInfo()
    } else if (this.currentUserWorkforceInfo) {
      this.setDefaultOwner(this.currentUserWorkforceInfo)
    }

    this.getProjects()
    this.getLocations()
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  },
  watch: {
    currentUserWorkforceInfo(newVal) {
      if (this.isNew) {
        this.setDefaultOwner(newVal)
      }
    },
  },
  computed: {
    ...mapGetters(['companyId', 'currentUserWorkforceInfo', 'isMobile']),
    canEdit() {
      return hasAccessRightForCollection('jobs', AccessRightWrite)
    },
    jobProgress() {
      return this.jobInfo.progress ?? 0
    },
    isNew() {
      return this.id === 'new'
    },
    jobInfoChanged() {
      return JSON.stringify(this.jobInfo) !== JSON.stringify(this.editedJobInfo)
    },
    collectionRef() {
      return db.collection(`companies/${this.companyId}/jobs`)
    },
    employeesCollectionRef() {
      return db.collection(`companies/${this.companyId}/workforce`)
    },
    projectsCollectionRef() {
      let result = db.collection(`companies/${this.companyId}/projects`)
      if (this.isNew) {
        result = result.where('archived', '==', false)
      }
      return result
    },
    locationsCollectionRef() {
      return db.collection(`companies/${this.companyId}/locations`)
    },
  },
  methods: {
    ...mapActions(['showError', 'showDeletionSuccess', 'uploadFiles']),
    selectedFilesChange(change) {
      this.selectedFiles = change
    },
    setDefaultOwner(owner) {
      this.jobInfo.owner = owner
      this.editedJobInfo.owner = owner
    },
    async createProjectAndSelect(projectData) {
      this.creatingProject = true
      try {
        const doc = await this.projectsCollectionRef.add(
          objects.withCreatedDataInfo(projectData)
        )
        const project = denormalized.project(projectData, doc.id)
        this.projects.push(project)
        this.editedJobInfo.project = project
        this.newProjectPopupVisible = false
      } catch (error) {
        this.showError(
          '<b>Failed to create project</b>\n' + error.message ??
            'Please try again later'
        )
        console.error(error)
      } finally {
        this.creatingProject = false
      }
    },
    async createNewLocationAndSelect(locationData) {
      this.creatingLocation = true
      try {
        const doc = await this.locationsCollectionRef.add(
          objects.withCreatedDataInfo(locationData)
        )
        const location = denormalized.location(locationData, doc.id)
        this.locations.push(location)
        this.editedJobInfo.location = location
        this.newLocationPopupVisible = false
      } catch (error) {
        this.showError(
          '<b>Failed to create location</b>\n' + error.message ??
            'Please try again later'
        )
        console.error(error)
      } finally {
        this.creatingLocation = false
      }
    },

    goBack() {
      this.$router.go(-1)
    },
    removeProjectSelection() {
      delete this.editedJobInfo.project
      this.$refs.projectSelect.blur()
      this.$refs.projectSelect.reset()
    },
    removeLocationSelection() {
      delete this.editedJobInfo.location
      this.$refs.locationSelect.blur()
      this.$refs.locationSelect.reset()
    },
    async getInfo() {
      this.loading = true
      const ref = this.collectionRef.doc(this.id)
      if (this.unsubscribe) {
        this.unsubscribe()
      }

      this.unsubscribe = ref.onSnapshot(
        (doc) => {
          this.jobInfo = doc.data()
          this.editedJobInfo = Object.assign(
            { status: constants.STATUS_PENDING, assignees: [] },
            this.jobInfo
          )

          this.loading = false
        },
        (error) => {
          console.error(error)
          this.loading = false
        }
      )
    },
    async getProjects() {
      try {
        this.projectsLoading = true
        const snapshot = await this.projectsCollectionRef.get()

        this.projects = snapshot.docs.map((doc) =>
          denormalized.project(doc.data(), doc.id)
        )
        if (this.projectId) {
          this.editedJobInfo.project = this.projects.find(
            (x) => x.id === this.projectId
          )
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.projectsLoading = false
      }
    },
    async getLocations() {
      try {
        this.locationsLoading = true

        const snapshot = await this.locationsCollectionRef.get()

        if (!snapshot.empty) {
          this.locations = snapshot.docs.map((doc) =>
            denormalized.location(doc.data(), doc.id)
          )
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.locationsLoading = false
      }
    },
    async saveEditedJobInfo() {
      this.saving = true
      try {
        if (!this.editedJobInfo.startDate && this.editedJobInfo.startTime) {
          throw 'Please specify date for your start time'
        }
        if (!this.editedJobInfo.dueDate && this.editedJobInfo.dueTime) {
          throw 'Please specify date for your end time'
        }

        if (this.editedJobInfo.startDate && this.editedJobInfo.dueDate) {
          if (this.editedJobInfo.startDate > this.editedJobInfo.dueDate) {
            throw 'Start date must not be after end date'
          }
          if (
            this.editedJobInfo.startTime &&
            this.editedJobInfo.dueTime &&
            new Date(this.editedJobInfo.startDate).getTime() ===
              new Date(this.editedJobInfo.dueDate).getTime()
          ) {
            if (
              this.editedJobInfo.startTime.HH > this.editedJobInfo.dueTime.HH
            ) {
              throw 'Start time must not be after end time'
            } else if (
              this.editedJobInfo.startTime.HH == this.editedJobInfo.dueTime.HH
            ) {
              if (
                this.editedJobInfo.startTime.mm > this.editedJobInfo.dueTime.mm
              ) {
                throw 'Start time must not be after end time'
              }
            }
          }
        }

        // cleanup assignees
        this.editedJobInfo.assignees = this.editedJobInfo.assignees.map((e) =>
          denormalized.workforce(e)
        )

        this.editedJobInfo.owner = denormalized.workforce(
          this.editedJobInfo.owner
        )

        if (this.editedJobInfo.project === undefined) {
          delete this.editedJobInfo.project
        }

        if (this.isNew) {
          const doc = await this.collectionRef.add(
            objects.withCreatedDataInfo(this.editedJobInfo)
          )
          if (this.selectedFiles.length > 0) {
            this.uploadFilesForTaskID(doc.id)
          }

          this.$router.replace({
            name: 'jobs',
          })
        } else {
          await this.collectionRef
            .doc(this.id)
            .set(
              objects.withUpdatedDataInfo({
                ...this.editedJobInfo,
                updatedAt: serverTimestamp,
              })
            )

          if (this.selectedFiles.length > 0) {
            this.uploadFilesForTaskID(this.id)
          }

          this.goBack()
        }
      } catch (error) {
        console.error(error)
        this.showError(error)
      } finally {
        this.saving = false
      }
    },
    async uploadFilesForTaskID(taskId) {
      const path = `companies/${this.companyId}/jobs/${taskId}`
      const storageRef = storage.ref().child(path)
      const files = this.selectedFiles.map((f) => ({
        file: f,
        ref: storageRef.child(f.name),
      }))

      this.uploadFiles(files)
    },
    async deleteJob() {
      const result = await this.$confirm(
        'Are you sure you want to delete this task?',
        { title: 'Please confirm' }
      )

      if (result === true) {
        // DELETE project
        try {
          await this.collectionRef.doc(this.id).delete()
          this.showDeletionSuccess('Task has been deleted.')
          this.goBack()
        } catch (error) {
          this.showError(
            error.message ?? 'Failed to delete, please try again later'
          )
        }
      }
    },
    selectStartDate(date) {
      this.editedJobInfo.startDate = date
      this.startDateMenu = false
    },
    selectDueDate(date) {
      this.editedJobInfo.dueDate = date
      this.dateMenu = false
    },
    selectStartTime(timeObject) {
      if (timeObject.displayTime.length === 0) {
        delete this.editedJobInfo.startTime
      }

      if (timeObject.data.HH.length === 0) {
        this.editedJobInfo.startTime.HH = '00'
      }
      if (timeObject.data.mm.length === 0) {
        this.editedJobInfo.startTime.mm = '00'
      }
    },
    selectDueTime(timeObject) {
      if (timeObject.displayTime.length === 0) {
        delete this.editedJobInfo.dueTime
      }

      if (timeObject.data.HH.length === 0) {
        this.editedJobInfo.dueTime.HH = '00'
      }
      if (timeObject.data.mm.length === 0) {
        this.editedJobInfo.dueTime.mm = '00'
      }
    },
    removeStartDate() {
      delete this.editedJobInfo.startDate
    },
    removeDueDate() {
      delete this.editedJobInfo.dueDate
    },
  },
}
</script>

<style lang="sass" scoped>
.mobile
  padding-bottom: 60px
</style>

<style lang="sass">
.vue__time-picker .dropdown ul li:not([disabled]).active, .vue__time-picker .dropdown ul li:not([disabled]).active:focus, .vue__time-picker .dropdown ul li:not([disabled]).active:hover
  background: $primary

.vue__time-picker input.display-time
  border: none !important
  background-color: $background !important
  @include hint
  height: 38px
</style>
